// src/pages/admin/AdminDealsManagement.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiAlertCircle
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { getDealImageUrl } from '../../utils/dealImageHelper';

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (v) => {
  try {
    return safeNumber(v).toLocaleString();
  } catch {
    return String(v || 0);
  }
};

const safeDate = (d) => {
  try {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('fr-FR');
  } catch {
    return '—';
  }
};

const computeStatusFromDates = (deal) => {
  try {
    const now = Date.now();
    const start = deal?.startDate ? new Date(deal.startDate).getTime() : null;
    const end = deal?.endDate ? new Date(deal.endDate).getTime() : null;

    if (end && end < now) return 'expired';
    if (start && start > now) return 'pending';

    const qty =
      typeof deal?.quantity === 'number'
        ? deal.quantity
        : safeNumber(deal?.quantity);
    const sold =
      typeof deal?.quantitySold === 'number'
        ? deal.quantitySold
        : safeNumber(deal?.quantitySold);

    if (qty !== -1 && qty > 0 && sold >= qty) {
      return 'sold_out';
    }

    return deal?.status || 'active';
  } catch {
    return deal?.status || 'active';
  }
};

const getStatusBadge = (status) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-red-100 text-red-800',
    sold_out: 'bg-orange-100 text-orange-800',
    draft: 'bg-gray-100 text-gray-800',
    inactive: 'bg-gray-100 text-gray-800'
  };
  const labels = {
    active: 'Actif',
    pending: 'En attente',
    expired: 'Expiré',
    sold_out: 'Épuisé',
    draft: 'Brouillon',
    inactive: 'Inactif'
  };
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        styles[status] || styles.draft
      }`}
    >
      {labels[status] || status || '—'}
    </span>
  );
};

const resolveMerchantName = (deal) => {
  if (!deal) return 'N/A';
  const m = deal.merchantId || deal.merchant || deal.merchantData || null;
  if (!m) return 'N/A';
  if (typeof m === 'string') return m;
  if (typeof m === 'object') {
    return (
      m.businessName ||
      m.name ||
      m.companyName ||
      m.company ||
      (m._id ? String(m._id) : 'N/A')
    );
  }
  return 'N/A';
};

const AdminDealsManagement = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState('');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const mountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    fetchDeals(page, perPage, debouncedSearch, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, statusFilter]);

  const fetchDeals = async (
    pageToFetch = 1,
    limitToFetch = 20,
    q = '',
    status = 'all'
  ) => {
    setLoading(true);
    try {
      const params = { page: pageToFetch, limit: limitToFetch };
      if (q) params.q = q;
      if (status && status !== 'all') params.status = status;

      const res = await API.get('/admin/deals', { params });
      const payload = res?.data ?? {};

      let list = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload.data)) list = payload.data;
      else if (Array.isArray(payload.docs)) list = payload.docs;
      else if (Array.isArray(payload.data?.data)) list = payload.data.data;
      else if (Array.isArray(payload.result)) list = payload.result;
      else if (Array.isArray(payload.items)) list = payload.items;

      const normalized = (list || []).map((d) => ({
        ...d,
        startDate: d.startDate || d.validFrom || null,
        endDate: d.endDate || d.validUntil || null
      }));

      if (!mountedRef.current) return;
      setDeals(normalized);

      const pageFromServer =
        payload.currentPage ??
        payload.page ??
        payload.data?.currentPage ??
        payload.meta?.currentPage ??
        null;
      const totalPagesPayload =
        payload.totalPages ??
        payload.meta?.totalPages ??
        payload.pagination?.totalPages ??
        null;
      const totalItems =
        payload.totalItems ??
        payload.total ??
        payload.meta?.total ??
        payload.pagination?.totalItems ??
        payload.pagination?.total ??
        null;
      const itemsPerPage =
        payload.itemsPerPage ??
        payload.limit ??
        payload.meta?.limit ??
        payload.pagination?.itemsPerPage ??
        limitToFetch;

      if (pageFromServer) setPage(Number(pageFromServer));
      if (itemsPerPage) setPerPage(Number(itemsPerPage));
      if (totalItems) {
        setTotal(Number(totalItems));
      } else {
        const headerTotal = res?.headers?.['x-total-count'];
        if (headerTotal) setTotal(Number(headerTotal));
        else setTotal(normalized.length || 0);
      }

      if (totalPagesPayload && pageToFetch > Number(totalPagesPayload)) {
        setPage(Number(totalPagesPayload));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des deals');
      if (mountedRef.current) setDeals([]);
      setTotal(0);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const askDelete = (deal) => {
    const id = deal?._id || deal?.id;
    if (!id) {
      toast.error('ID du deal manquant');
      return;
    }
    setDeleteId(id);
    setDeleteTitle(deal?.title || deal?.name || 'élément');
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoadingDelete(true);

    try {
      await API.delete(`/admin/deals/${deleteId}`);
      toast.success('Deal supprimé avec succès');
      setShowDelete(false);
      setDeleteId(null);
      setDeleteTitle('');
      await fetchDeals(page, perPage, debouncedSearch, statusFilter);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erreur lors de la suppression'
      );
    } finally {
      if (mountedRef.current) setLoadingDelete(false);
    }
  };

  const handleApprove = async (dealId) => {
    if (!dealId) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await API.put(`/admin/deals/${dealId}/approve`);
      toast.success('Deal approuvé');
      await fetchDeals(page, perPage, debouncedSearch, statusFilter);
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const openEdit = async (deal) => {
    const id = deal?._id || deal?.id;
    if (!id) {
      toast.error('ID du deal manquant');
      return;
    }

    try {
      let res;
      try {
        res = await API.get(`/admin/deals/${id}`);
      } catch (_) {
        try {
          res = await API.get(`/deals/id/${id}`);
        } catch (_) {
          try {
            res = await API.get(`/merchant/deals/${id}`);
          } catch (_) {
            res = null;
          }
        }
      }

      if (res) {
        const payload = res?.data ?? {};
        const fullDeal = Array.isArray(payload)
          ? payload[0]
          : payload?.data ?? payload;
        if (fullDeal) {
          navigate(`/merchant/deals/${id}/edit`, {
            state: { deal: fullDeal, adminMode: true }
          });
          return;
        }
      }
    } catch (_) {}

    navigate(`/merchant/deals/${id}/edit`, { state: { deal, adminMode: true } });
  };

  const filteredDealsCount = deals.length;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || filteredDealsCount) / perPage)),
    [total, filteredDealsCount, perPage]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  const statsCount = useMemo(() => {
    const activeCount = deals.filter((d) => {
      const s = d.status || computeStatusFromDates(d);
      return s === 'active';
    }).length;

    const pendingCount = deals.filter((d) => {
      const s = d.status || computeStatusFromDates(d);
      return s === 'pending';
    }).length;

    const soldOutCount = deals.filter((d) => {
      const s = d.status || computeStatusFromDates(d);
      return s === 'sold_out';
    }).length;

    const expiredCount = deals.filter((d) => {
      const s = d.status || computeStatusFromDates(d);
      return s === 'expired';
    }).length;

    return {
      active: activeCount,
      pending: pendingCount,
      soldOut: soldOutCount,
      expired: expiredCount
    };
  }, [deals]);

  return (
    <div className="p-6">
      <ConfirmDeleteModal
        open={showDelete}
        title={`Supprimer : ${deleteTitle}`}
        description="Cette action supprimera définitivement ce deal. Êtes-vous certain(e) ?"
        onCancel={() => {
          if (!loadingDelete) {
            setShowDelete(false);
            setDeleteId(null);
            setDeleteTitle('');
          }
        }}
        onConfirm={confirmDelete}
        loading={loadingDelete}
      />

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Deals
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez tous vos deals en un seul endroit
            </p>
          </div>
          <Link
            to="/merchant/deals/new"
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiPlus className="mr-2" />
            Créer un Deal
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher des deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Recherche des deals"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par statut"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="pending">En attente</option>
              <option value="expired">Expiré</option>
              <option value="sold_out">Épuisé</option>
              <option value="draft">Brouillon</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Deals</p>
              <p className="text-2xl font-bold text-gray-900">
                {total || deals.length}
              </p>
            </div>
            <FiClock className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-green-600">
                {statsCount.active}
              </p>
            </div>
            <FiCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">
                {statsCount.pending}
              </p>
            </div>
            <FiClock className="text-yellow-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Épuisés</p>
              <p className="text-2xl font-bold text-orange-600">
                {statsCount.soldOut}
              </p>
            </div>
            <FiAlertCircle className="text-orange-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expirés</p>
              <p className="text-2xl font-bold text-red-600">
                {statsCount.expired}
              </p>
            </div>
            <FiXCircle className="text-red-500 text-2xl" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marchand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                    <p className="mt-2 text-gray-500">Chargement...</p>
                  </td>
                </tr>
              ) : deals.length > 0 ? (
                deals.map((deal) => {
                  const id = deal?._id || deal?.id;
                  const resolvedStatus =
                    deal?.status || computeStatusFromDates(deal);
                  const merchantName = resolveMerchantName(deal);

                  const qty =
                    typeof deal.quantity === 'number'
                      ? deal.quantity
                      : safeNumber(deal.quantity);
                  const sold =
                    typeof deal.quantitySold === 'number'
                      ? deal.quantitySold
                      : safeNumber(deal.quantitySold);
                  const qtyDisplay =
                    qty === -1 ? 'Illimité' : `${sold} / ${qty}`;

                  const merchantIdValue =
                    deal?.merchantId?._id || deal?.merchantId || '—';

                  const discountPercent = safeNumber(
                    deal?.discountPercentage
                  );

                  return (
                    <tr
                      key={id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={getDealImageUrl(deal, 0, '/placeholder.jpg')}
                            alt={deal?.title || 'deal'}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/placeholder.jpg';
                            }}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                              {deal?.title || 'Sans titre'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {deal?.category || '-'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {merchantName || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          id: {String(merchantIdValue)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(deal?.discountedPrice)} MAD
                          </div>
                          <div className="text-gray-500 line-through">
                            {formatCurrency(deal?.originalPrice)} MAD
                          </div>
                          <div
                            className={`font-medium ${
                              discountPercent > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {discountPercent > 0
                              ? `-${discountPercent}%`
                              : `${discountPercent}%`}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {qtyDisplay}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(resolvedStatus)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>Début: {safeDate(deal?.startDate)}</div>
                        <div>Fin: {safeDate(deal?.endDate)}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/deals/${deal.slug || id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir sur le site"
                          >
                            <FiEye size={18} />
                          </Link>

                          {resolvedStatus === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleApprove(id)}
                              className="text-green-600 hover:text-green-900"
                              title="Approuver"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiCheckCircle size={18} />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => openEdit(deal)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Modifier (admin)"
                          >
                            <FiEdit size={18} />
                          </button>

                          <button
                            type="button"
                            onClick={() => askDelete(deal)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                            disabled={isSubmitting || loadingDelete}
                            aria-disabled={isSubmitting || loadingDelete}
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <FiClock className="mx-auto text-4xl mb-2" />
                    <p>Aucun deal trouvé</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>

            <div className="ml-4 text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>

            <div className="ml-4 text-sm text-gray-600">
              <span className="mr-2">Afficher</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            {total > 0
              ? `Total: ${total} résultats`
              : `${deals.length} affichés`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDealsManagement;